import cx from 'classnames';
import type { ReactNode } from 'react';

import type { Option } from '@zen/DesignSystem/types';

import FormHiddenInput from '../FormHiddenInput';
import FormNumberInput from '../FormNumberInput';
import FormSelect from '../FormSelect';

interface LabelsConfig {
  height: string;
  length: string;
  width: string;
}

interface Props<T> {
  className?: string;
  hasUnit?: boolean;
  hideErrorMessage?: boolean;
  initialDimensionsDisabled?: boolean;
  isDisabled?: boolean;
  labels?: LabelsConfig;
  layout?: '2columns' | '3columns';
  name: string;
  options: Option<T>[];
  thirdDimensionName?: string;
}

const FormDimensionsInput = <T extends {}>(props: Props<T>) => {
  const {
    className,
    hideErrorMessage,
    hasUnit = false,
    thirdDimensionName = 'height',
    isDisabled,
    initialDimensionsDisabled,
    labels = {
      height: 'Height',
      length: 'Length',
      width: 'Width'
    },
    layout = '2columns',
    name,
    options
  } = props;

  const classNames: string = cx(
    {
      'grid-cols-2': layout === '2columns',
      'grid-cols-3': layout === '3columns'
    },
    'grid gap-x-4'
  );
  const wrapperClassNames: string = cx(classNames, className);
  const unitFieldName: string = hasUnit ? `${name}.unit` : `${name}.metric`;

  const renderDimensionInputs = (): ReactNode => {
    const fields: ReactNode = (
      <>
        <FormNumberInput
          disabled={initialDimensionsDisabled || isDisabled}
          hideErrorMessage={hideErrorMessage}
          label={labels.length}
          min={0}
          name={`${name}.length`}
          placeholder="0.0"
          step="any"
        />
        <FormNumberInput
          disabled={initialDimensionsDisabled || isDisabled}
          hideErrorMessage={hideErrorMessage}
          label={labels.width}
          min={0}
          name={`${name}.width`}
          placeholder="0.0"
          step="any"
        />
      </>
    );

    if (layout === '2columns') {
      return <div className="grid grid-cols-2 gap-x-4">{fields}</div>;
    }

    return fields;
  };

  return (
    <div data-testid="dimensions">
      <div className={wrapperClassNames}>
        {renderDimensionInputs()}
        <div className="flex items-end space-x-1">
          <FormNumberInput
            className="flex-1"
            disabled={isDisabled}
            hideErrorMessage={hideErrorMessage}
            label={labels.height}
            min={0}
            name={`${name}.${thirdDimensionName}`}
            placeholder="0.0"
            step="any"
          />
          <FormSelect
            hideErrorMessage={hideErrorMessage}
            hideLabel={true}
            isDisabled={initialDimensionsDisabled || isDisabled}
            isSearchable={false}
            name={unitFieldName}
            options={options}
            placeholder="cm"
            renderMenuInPortal={true}
          />
        </div>
      </div>
      <div className="mb-4 -mt-4">
        <FormHiddenInput name={`${name}.length`} />
        <FormHiddenInput name={`${name}.width`} />
        <FormHiddenInput name={`${name}.${thirdDimensionName}`} />
      </div>
    </div>
  );
};

export type { Props as FormDimensionsInputProps };

export default FormDimensionsInput;
